import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';

function useDebouncedValue(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

/**
 * AsyncSearchSelect
 * Props:
 * - label: string
 * - value: string | null
 * - onChange: (id, option) => void
 * - fetchPage: ({ q, offset, limit }) => Promise<Array<{ id, label }>>
 * - limit: number (default 50)
 * - disabled: boolean
 * - placeholder: string
 * - selectedLabel: string
 * - fullWidth: boolean
 * - size: 'small' | 'medium'
 * - sx: object (MUI sx for Autocomplete)
 * - textFieldProps: object (extra props for TextField)
 * - autocompleteProps: object (extra props for Autocomplete)
 */
export default function AsyncSearchSelect({
  label,
  value,
  onChange,
  fetchPage,
  limit = 50,
  disabled = false,
  placeholder,
  selectedLabel = '',
  fullWidth = true,
  size = 'small',
  sx,
  textFieldProps,
  autocompleteProps,
}) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebouncedValue(query, 300);
  const listboxRef = useRef(null);

  // Reset when query changes
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    async function loadFirst() {
      setLoading(true);
      try {
        const data = await fetchPage({ q: debouncedQuery, offset: 0, limit });
        if (!cancelled) {
          setOptions(data);
          setHasMore(data.length >= limit);
        }
      } catch (e) {
        if (!cancelled) {
          setOptions([]);
          setHasMore(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    loadFirst();
    return () => { cancelled = true; };
  }, [debouncedQuery, open, fetchPage, limit]);

  // Load when opened
  useEffect(() => {
    if (!open) return;
    if (options.length === 0 && !loading) {
      setLoading(true);
      fetchPage({ q: debouncedQuery, offset: 0, limit })
        .then((data) => {
          setOptions(data);
          setHasMore(data.length >= limit);
        })
        .catch(() => {
          setOptions([]);
          setHasMore(false);
        })
        .finally(() => setLoading(false));
    }
  }, [open]);

  // Infinite scroll handler
  const handleListboxScroll = (event) => {
    const target = event.currentTarget;
    const nearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 24;
    if (nearBottom && hasMore && !loading) {
      setLoading(true);
      fetchPage({ q: debouncedQuery, offset: options.length, limit })
        .then((data) => {
          setOptions((prev) => [...prev, ...data]);
          setHasMore(data.length >= limit);
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  };

  // Compute selected option: prefer one from loaded options; otherwise synthesize from value + selectedLabel
  const selectedOption = useMemo(() => {
    if (!value) return null;
    const valueStr = String(value).trim();
    const found = options.find(o => String(o?.id ?? '').trim() === valueStr);
    return found || { id: valueStr, label: selectedLabel || '' };
  }, [value, options, selectedLabel]);

  return (
    <Autocomplete
      {...(autocompleteProps || {})}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      value={selectedOption}
      getOptionLabel={(option) => option?.label || ''}
      isOptionEqualToValue={(opt, val) => String(opt?.id ?? '').trim() === String(val?.id ?? '').trim()}
      loading={loading}
      ListboxProps={{ onScroll: handleListboxScroll, ref: listboxRef }}
      onChange={(_, option) => onChange(option ? option.id : '', option || null)}
      filterOptions={(x) => x} // disable client filtering; we do server-side
      fullWidth={fullWidth}
      sx={{
        ...(fullWidth ? { width: '100%' } : null),
        ...(sx || null),
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          {...(textFieldProps || {})}
          label={label}
          placeholder={placeholder}
          size={size}
          fullWidth={fullWidth}
          onChange={(e) => {
            setQuery(e.target.value);
            if (typeof textFieldProps?.onChange === 'function') {
              textFieldProps.onChange(e);
            }
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          disabled={disabled}
        />
      )}
    />
  );
}